import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { HttpService } from '../../../../core/services/http/http.service';
import { catchError, first, tap } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

type LoginOtpErrorCode = 'OTP_INVALID' | 'OTP_EXPIRED' | 'OTP_LOCKED';

@Component({
	selector: 'bcb-otp',
	templateUrl: './otp.component.html',
	styleUrls: ['./otp.component.scss'],
})
export class OtpComponent implements OnInit, OnDestroy {
	verificationForm: UntypedFormGroup;

	entity_id: string = '';
	@Input()
	token: string = '';
	contactId?: string;
	systemUser?: string;
	error?: string;
	loading: boolean = false;
	message?: string;
	locked = false;
	lockRetryAfterSeconds = 0;

	get lockRetryLabel(): string {
		const seconds = this.lockRetryAfterSeconds;
		if (seconds > 60) {
			const minutes = Math.ceil(seconds / 60);
			return `${minutes} minute${minutes === 1 ? '' : 's'}`;
		}
		return `${seconds} second${seconds === 1 ? '' : 's'}`;
	}

	unsubscribe$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		false
	);
	private lockTimer?: ReturnType<typeof setInterval>;

	constructor(
		private readonly http: HttpService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly _snackBar: MatSnackBar
	) {
		this.verificationForm = new UntypedFormGroup({
			otp: new UntypedFormControl(undefined, [
				Validators.minLength(5),
				Validators.maxLength(5),
				Validators.required,
				Validators.pattern('[0-9]*'),
			]),
		});
		this.contactId = this.http.getEntityContact()?.id;
	}

	ngOnInit(): void {
		this.route.params.subscribe((params: Params) => {
			this.entity_id = params.entity_id;
			this.token = this.token || params.token;
			this.systemUser =
				this.route.snapshot.queryParamMap.get('systemUser') ??
				undefined;

			if (this.systemUser) {
				this.http.setSystemUser(this.systemUser);
			}
			if (this.http.getToken()) {
				this.http.getTokenPayload();
				this.router.navigate(['/']);
			} else {
				this.sendOTP();
			}
		});
	}

	sendOTP(): void {
		if (this.locked) {
			return;
		}
		this.error = undefined;
		this.loading = true;
		if (!this.contactId) {
			this.legacyAuthLogin();
		} else {
			this.authLogin();
		}
	}

	authLogin(): void {
		this.http
			.authLoginV2({
				entityId: this.entity_id,
				token: this.token,
				systemUser: this.systemUser,
				// @ts-ignore
				contactId: this.contactId,
			})
			.pipe(
				tap((data) => {
					this.message = data.message;
					this.loading = false;
				}),
				catchError((error) => {
					this.handleLoginError(error);
					return of(undefined);
				})
			)
			.subscribe();
	}

	legacyAuthLogin(): void {
		this.http
			.authLogin({
				entity_id: this.entity_id,
				token: this.token,
				systemUser: this.systemUser,
			})
			.pipe(first((data) => !!data))
			.subscribe(
				(data) => {
					this.message = data.message;
					this.loading = false;
				},
				(error) => {
					this.handleLoginError(error);
				}
			);
	}

	onOtpChange(otp: string): void {
		if (!this.locked) {
			this.error = undefined;
		}
		this.verificationForm.get('otp')?.setValue(otp);
	}

	onSubmit() {
		if (this.locked) {
			return;
		}
		if (this.verificationForm.valid) {
			this.loading = true;
			this.verificationForm.disable();
			if (this.contactId) {
				this.verifyOTP(
					this.verificationForm.value.otp,
					this.entity_id,
					this.token,
					this.contactId
				);
			} else {
				this.legacyVerifyOTP(
					this.verificationForm.value.otp,
					this.entity_id,
					this.token
				);
			}
		}
	}

	verifyOTP(
		otp: string,
		entity_id: string,
		token: string,
		contactId: string
	): void {
		this.http
			.authVerifyV2({
				otp,
				entity_id,
				token,
				contactId,
				systemUserId: this.systemUser,
			})
			.pipe(
				tap((data) => {
					const token = data.access_token;
					sessionStorage.setItem('onboarding_token', token);
					this.loading = false;
					this.router.navigate(['/']);
				}),
				catchError((error) => {
					this.handleVerifyError(error);
					return of(undefined);
				})
			)
			.subscribe();
	}

	legacyVerifyOTP(otp: string, entity_id: string, token: string): void {
		this.http
			.authVerify({
				otp,
				entity_id,
				token,
				systemUserId: this.systemUser,
			})
			.pipe(first((data) => !!data))
			.subscribe(
				(data) => {
					const token = data.access_token;
					sessionStorage.setItem('onboarding_token', token);
					this.loading = false;
					this.router.navigate(['/']);
				},
				(error) => {
					this.handleVerifyError(error);
				}
			);
	}

	handleVerifyError(error: any): void {
		const message = this.extractErrorMessage(error);
		const code = error?.error?.code as LoginOtpErrorCode | undefined;

		if (!error?.status) {
			this.error =
				'Something went wrong. Please check your OTP and try again';
			this.showError(this.error);
		} else if (code === 'OTP_LOCKED') {
			this.applyLockout(error.error?.retryAfterSeconds);
			this.showError(message);
		} else if (code === 'OTP_EXPIRED') {
			this.verificationForm.get('otp')?.setValue('');
			this.showError(
				`${message} Use Send code again to request a new code.`
			);
		} else if (code === 'OTP_INVALID') {
			const remaining = error.error?.attemptsRemaining;
			this.showError(
				remaining != null
					? `${message} ${remaining} attempts remaining.`
					: message
			);
		} else {
			this.showError(message);
		}

		this.loading = false;
		this.verificationForm.enable();
	}

	ngOnDestroy(): void {
		this.clearLockTimer();
		this.unsubscribe$.next(true);
		this.unsubscribe$.complete();
	}

	private handleLoginError(error: any): void {
		this.loading = false;
		const code = error?.error?.code as LoginOtpErrorCode | undefined;
		if (code === 'OTP_LOCKED' || error?.status === 429) {
			this.applyLockout(error.error?.retryAfterSeconds);
			this.showError(this.extractErrorMessage(error));
			return;
		}

		this.error = `Something went wrong. Please contact support for assistance`;
		this._snackBar.open(this.error, undefined, {
			panelClass: 'bg-danger',
		});
	}

	private applyLockout(retryAfterSeconds?: number): void {
		const seconds = Number(retryAfterSeconds);
		this.clearLockTimer();
		// A 429 without a positive retry window (gateway rate limit, missing body)
		// must not stick the form in a locked state with no countdown to clear it.
		if (!Number.isFinite(seconds) || seconds <= 0) {
			this.locked = false;
			this.lockRetryAfterSeconds = 0;
			return;
		}

		this.locked = true;
		this.lockRetryAfterSeconds = seconds;
		this.lockTimer = setInterval(() => {
			this.lockRetryAfterSeconds = Math.max(
				0,
				this.lockRetryAfterSeconds - 1
			);
			if (this.lockRetryAfterSeconds === 0) {
				this.locked = false;
				this.clearLockTimer();
			}
		}, 1000);
	}

	private clearLockTimer(): void {
		if (this.lockTimer) {
			clearInterval(this.lockTimer);
			this.lockTimer = undefined;
		}
	}

	private extractErrorMessage(error: any): string {
		const raw = error?.error?.message;
		if (Array.isArray(raw)) {
			return raw[0] || 'Something went wrong';
		}
		if (typeof raw === 'string' && raw.length) {
			return raw;
		}
		return 'Something went wrong';
	}

	private showError(message: string): void {
		this._snackBar.open(message, undefined, {
			duration: 5000,
			verticalPosition: 'top',
			horizontalPosition: 'right',
			panelClass: 'bg-danger',
		});
	}
}
